import type { AxiosResponse } from 'axios'

import { VERSION_1 } from '@/libs/constants/b2bApiEndpoints'
import { CUSTOMER_USER } from '@/libs/constants/user'
import type {
  CreateInvitationRequest,
  DeleteUserRequest,
  UpdateCustomerSapBody,
  UpdateInvitationBody,
  UpdateUserBody,
  UpdateUserEmailBody,
} from '@/types/api/admin'
import environment from '@/environments/environment.dev'

import HttpClient from '../apiHttpClient'

const apiClient = new HttpClient(environment.adminFAPIUrl)

const USER_INVITATION = `/${VERSION_1}/user/invitation`

export interface GetUserListParams {
  current: number
  pageSize: number
  sortBy?: string
  orderBy?: string
  searchTerm?: string
  selectedLetter?: string
  country?: string
  userType?: string
  companyName?: string
  firstName?: string
  lastName?: string
  email?: string
  status?: string
}

const getUserList = async ({
  current,
  pageSize,
  sortBy,
  orderBy,
  searchTerm,
  selectedLetter,
  country,
  userType = CUSTOMER_USER,
  companyName,
  firstName,
  lastName,
  email,
  status,
}: GetUserListParams) => {
  const queryParams = {
    pageNumber: String(current),
    pageSize: String(pageSize),
    ...(sortBy && { sortBy }),
    ...(orderBy && { orderBy }),
    ...(searchTerm && { searchTerm }),
    ...(selectedLetter && { selectedLetter }),
    ...(country && { country }),
    ...(companyName && { companyName }),
    ...(firstName && { firstName }),
    ...(lastName && { lastName }),
    ...(email && { email }),
    ...(status && { Status: status }),
  }

  const endpoint = userType === 'technical' ? 'technicaluser' : userType

  const parameters = new URLSearchParams(queryParams)
  return apiClient.get(`${VERSION_1}/${endpoint}/list?${parameters}`)
}

const getUserDetailsById = async (userId: string) => {
  return apiClient.get(`${VERSION_1}/user/detail/${userId}`)
}

const deleteUserAccount = async (userId: string, deleteBy: string) => {
  const body: DeleteUserRequest = { id: userId, deleteBy }
  return apiClient.delete(`${VERSION_1}/user`, { body })
}

const editUser = (userId: string) => {
  return async (body: UpdateUserBody) => {
    return apiClient.put(`${VERSION_1}/user/detail`, {
      body: {
        userId,
        ...body,
      },
    })
  }
}

const submitResetUserPassword = async (userId: string, countryId: string) => {
  return apiClient.post(`${VERSION_1}/user/resetpassword`, {
    body: {},
    params: {
      userId,
      countryId,
    },
  })
}

const editUserEmailAddress = (userId: string) => {
  return async (body: UpdateUserEmailBody) => {
    return apiClient.post(`${VERSION_1}/user/changeemail/${userId}`, {
      body,
    })
  }
}

const editCustomerSap = (userId: string) => {
  return async (body: UpdateCustomerSapBody) => {
    return apiClient.put(`${VERSION_1}/user/sapcustomerid`, {
      body: {
        userId,
        ...body,
      },
    })
  }
}

const exportUserList = async (userType: string) => {
  return apiClient.get(`${VERSION_1}/user/exportuser/summary`, {
    params: {
      userType,
    },
  })
}

const revokeInvitation = async (invitationId: string, reactivateRevoke = false) => {
  const body = {
    invitationId,
    reactivateRevoke,
  }
  return apiClient.put(`${USER_INVITATION}/revoke`, { body })
}

const deleteInvitation = async (invitationId: string, countryId: string) => {
  const body = {
    invitationId,
    countryId,
  }
  return apiClient.delete(`${USER_INVITATION}`, { body })
}

const inviteNewUser = async (body: CreateInvitationRequest) => {
  return apiClient.post(USER_INVITATION, { body })
}

const getInvitedUserDetailsById = async (invitationId: string) => {
  return apiClient.get(`${USER_INVITATION}/${invitationId}`)
}

const updateInvitation = (invitationId: string) => {
  return async (body: UpdateInvitationBody) =>
    apiClient.put(`${USER_INVITATION}/update`, {
      body: {
        ...body,
        invitationId,
      },
    })
}

const uploadInvitations = async (file: FormData) => {
  const url = `${VERSION_1}/user/upload-invitation`

  return apiClient.post(url, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    body: file,
  })
}

const validatePhoneNumber = async (phoneNumber: string): Promise<AxiosResponse<boolean>> => {
  return apiClient.get(`${VERSION_1}/user/isvalid`, {
    params: { phoneNumber },
  })
}

const checkEmailExists = async (email: string): Promise<AxiosResponse<boolean>> => {
  return apiClient.get(`${VERSION_1}/user/${encodeURIComponent(email)}/isexist`)
}

export const adminFapiUserManagement = {
  getUserList,
  getUserDetailsById,
  deleteUserAccount,
  editUser,
  submitResetUserPassword,
  editUserEmailAddress,
  editCustomerSap,
  exportUserList,
  revokeInvitation,
  deleteInvitation,
  inviteNewUser,
  getInvitedUserDetailsById,
  updateInvitation,
  uploadInvitations,
  validatePhoneNumber,
  checkEmailExists,
}
